/* eslint-disable react-hooks/exhaustive-deps */

import { useAppContext } from '@contexts/app';
import { getErrorStatus } from '@services/queryErrors';
import { useQueryParams } from '@hooks/queryParams';
import { useGetAdmin } from '@services/panel/admins/get';
import { useGetUser } from '@services/resources/users/old/get';
import { redeemSignInExchange } from '@services/auth/signInExchange';
import { reportAuthError } from '@services/auth/report';
import { getUserData } from '@utils/getUserData';
import { localStore } from '@utils/localStore';
import { modelUserData } from '@utils/userData';
import { uuidRegEx } from '@utils/consts';
import * as Sentry from '@sentry/react';
import { useEffect, useRef } from 'react';

export const useAuth = () => {
  const {
    params: { exchange },
    setParam,
  } = useQueryParams();

  const { setData } = useAppContext();

  const localAdmin = getUserData('admin');
  const localUser = getUserData('user');

  const admin = useGetAdmin(localAdmin?.id);
  const user = useGetUser(localUser?.id);
  const redeemedExchangeRef = useRef<string | null>(null);

  useEffect(() => {
    if (!admin.data || admin.isLoading) return;
    const { data } = admin.data;
    if (!localUser?.id) Sentry.setUser({ id: data.id, segment: 'admin' });

    setData((prev) => ({
      ...prev,
      admin: {
        data,
        isLogged: true,
      },
    }));
  }, [setData, admin.data, admin.isLoading]);

  useEffect(() => {
    if (!user.data || user.isLoading) return;
    const { data, onDeckAlerts, hotSeatAlerts } = user.data;
    Sentry.setUser({ id: data.id, segment: data.primaryRole });
    Sentry.setTag('tenant', data.tenant?.slug);

    setData((prev) => ({
      ...prev,
      ...modelUserData(data),
      isLogged: true,
      isDropdownEnabled: data.primaryRole !== 'field_user',
      onDeckError: onDeckAlerts,
      hotSeatError: hotSeatAlerts,
    }));
  }, [setData, user.data, user.isLoading]);

  useEffect(() => {
    if (admin.isLoading || getErrorStatus(admin.error) !== 401) return;
    setData((prev) => ({ ...prev, admin: { ...prev.admin, isLogged: false } }));
  }, [setData, admin.error, admin.isLoading]);

  useEffect(() => {
    if (user.isLoading || getErrorStatus(user.error) !== 401) return;
    setData((prev) => ({ ...prev, isLogged: false }));
  }, [setData, user.error, user.isLoading]);

  useEffect(() => {
    if (!exchange || redeemedExchangeRef.current === exchange) return;
    redeemedExchangeRef.current = exchange;
    setData((prev) => ({ ...prev, isResolvingAuth: true }));
    redeemSignInExchange(exchange)
      .then((resp) => {
        const id = resp?.data?.id;
        if (!id || !uuidRegEx.test(id)) {
          reportAuthError('redeem', new Error('Sign-in exchange redeem returned no user id'));
          return;
        }
        const item = localStore.getItem('app-user');
        const parsed =
          typeof item === 'string' ? (JSON.parse(item) as Record<string, string>) : item;
        localStore.updateItem('app-user', { ...parsed, id });
      })
      .catch((error: unknown) => reportAuthError('redeem', error))
      .finally(() => {
        setParam('exchange', '');
        setData((prev) => ({ ...prev, isResolvingAuth: false }));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exchange]);
};
